/**
 * PageHeader - antd 6 兼容的页头组件
 *
 * 用于替换 antd 3 中已移除的 PageHeader 组件。
 * 保持与 antd 3 PageHeader 相同的 API，便于无缝迁移。
 */
import React from 'react'
import { Flex, Typography, Button, Space } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import type { CSSProperties, ReactNode } from 'react'
import './index.less'

const { Title, Text } = Typography

export interface PageHeaderProps {
  /** 页面标题 */
  title?: ReactNode
  /** 副标题 */
  subTitle?: ReactNode
  /** 返回按钮回调，传入则显示返回箭头 */
  onBack?: () => void
  /** 自定义返回图标 */
  backIcon?: ReactNode
  /** 右侧额外内容 */
  extra?: ReactNode
  /** 头像 */
  avatar?: ReactNode
  /** 标签 */
  tags?: ReactNode
  /** 面包屑 */
  breadcrumb?: ReactNode
  /** 底部内容 */
  footer?: ReactNode
  /** 透明背景模式（true 时无背景色和 padding） */
  ghost?: boolean
  /** 自定义类名 */
  className?: string
  /** 自定义样式 */
  style?: CSSProperties
  /** 子元素 */
  children?: ReactNode
}

/**
 * PageHeader 组件
 *
 * 用于页面顶部展示页面标题、返回按钮、操作按钮等。
 * API 与 antd 3 PageHeader 兼容。
 *
 * @example
 * ```tsx
 * import PageHeader from '@/components/PageHeader'
 *
 * <PageHeader
 *   title="页面标题"
 *   subTitle="副标题说明"
 *   onBack={() => history.goBack()}
 *   extra={<Button type="primary">操作</Button>}
 * />
 * ```
 */
export function PageHeader({
  title,
  subTitle,
  onBack,
  backIcon,
  extra,
  avatar,
  tags,
  breadcrumb,
  footer,
  ghost = false,
  className = '',
  style,
  children
}: PageHeaderProps) {
  // 构建类名
  const classNames = [
    'ant-page-header',
    ghost ? 'ant-page-header-ghost' : '',
    className
  ]
    .filter(Boolean)
    .join(' ')

  // 渲染返回按钮
  const renderBack = () => {
    if (!onBack) return null
    return (
      <div className="ant-page-header-back">
        <Button
          type="text"
          className="ant-page-header-back-button"
          onClick={onBack}
          icon={backIcon || <ArrowLeftOutlined />}
        />
      </div>
    )
  }

  // 渲染标题区域
  const renderTitle = () => {
    if (!title && !subTitle && !avatar && !tags && !extra) return null

    return (
      <Flex
        className="ant-page-header-heading"
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={12}
      >
        <Flex align="center" gap={12} className="ant-page-header-heading-left">
          {renderBack()}
          {avatar && <div className="ant-page-header-avatar">{avatar}</div>}
          <Space className="ant-page-header-heading-title-wrapper" size={8}>
            {title && (
              <Title
                level={4}
                className="ant-page-header-heading-title"
                style={{ margin: 0 }}
              >
                {title}
              </Title>
            )}
            {subTitle && (
              <Text
                type="secondary"
                className="ant-page-header-heading-sub-title"
              >
                {subTitle}
              </Text>
            )}
            {tags && <div className="ant-page-header-heading-tags">{tags}</div>}
          </Space>
        </Flex>
        {extra && (
          <Space className="ant-page-header-heading-extra">{extra}</Space>
        )}
      </Flex>
    )
  }

  return (
    <div className={classNames} style={style}>
      {breadcrumb && (
        <div className="ant-page-header-breadcrumb">{breadcrumb}</div>
      )}
      {renderTitle()}
      {children && <div className="ant-page-header-content">{children}</div>}
      {footer && <div className="ant-page-header-footer">{footer}</div>}
    </div>
  )
}

export default PageHeader
